import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { setNewMyTasks, storePinTask } from "../../actions/task";
import { getFocusedSection, setExpanded, setPinTask, setShowFormPopup, setTask, setTaskOnBlur } from "../../actions/taskSidebar";
import { label, tooltip } from "../../config";
import { dateFormat, icon, notifyIcon, number, quote } from "../../config/constants";
import { formatDate, formatDateWithoutTimeZone, formatDateWithoutYear, formatDateWithoutYearWithoutTimeZone, getDateAtMidnight } from "../../helper/common";
import store from "../../store/index";
import { getNotification, isTrueBit, prependString } from "../../utils/common";
import { DragRowWithDragEnd, GridContext } from "../../utils/kendo";
import { completeAndForward, getProjectStages, getTaskDetails, getTasks, openSidebar } from "../Tasks/tasks.service";
import NewTabPopUp from "./NewTabPopUp";
import NudgeButton from "./Views/NudgeButton";
import { filterAssignees, updateGrid } from "./tasks.common";
import { dateDiff, formatTags } from "./tasks.constants";
import { pinTaskHelper, setStageName } from "./tasks.helper";

/**
 * handles click outside of dropdown
 * @param {Object} props
 * @param {Ref} dropdownRef
 * @author {Sarthak Arora}
 */
const useClickOutside = (props, dropdownRef) => {
  const taskGridContext = React.useContext(GridContext);
  const { exitEdit } = taskGridContext;

  useEffect(() => {
    const handleClickOutside = (event) => {
      /**  Check if click is on a dropdown option or DatePicker popup*/
      const isDropdownOption = event.target.closest(".k-list-item") || event.target.closest(".k-popup");
      const isDatePickerPopup =
        event.target.closest(".k-calendar") || event.target.closest(".k-datepicker-popup") || event.target.closest(".k-calendar-popup");

      if (
        props.dataItem.inEdit === props.field &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !isDropdownOption &&
        !isDatePickerPopup
      ) {
        exitEdit();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [props.dataItem.inEdit, props.field]);
};

/**
 * handles completion of task from grid
 * @param {Object} task
 */
const taskCompleteAction = async (task) => {
  if (task) {
    const state = store.getState();
    const payload = {
      taskHistoryId: task.taskHistoryId,
      taskId: task.Id,
      userId: state.auth.user.id,
      positionId: state?.userPosition?.positionId,
    };
    const response = await completeAndForward(payload, task.CurrentProject);
    if (response) {
      getTasks(null, true);
    }
  }
};

export const getCompleteIcon = (row) => {
  const clickHandler = () => {
    if (!isTrueBit(row, "IsLocked") && !(row.IsTaskComplete || row.IsTaskClosed)) {
      taskCompleteAction(row);
    }
  };

  const isCompleted = row.IsTaskComplete || row.IsTaskClosed;
  const noAccessToCurrentFlow = isTrueBit(row, "IsLocked") && !(row.IsTaskComplete || row.IsTaskClosed);
  const canComplete = !isTrueBit(row, "IsLocked") && !(row?.IsTaskComplete || row?.IsTaskClosed);


  if (!canComplete && !noAccessToCurrentFlow && !isCompleted) {
    return <span id='mark-as-complete-icon' className='ml-1 d-flex justify-content-center align-items-center'></span>;
  }

  const getButtonClass = () => {
    if (isCompleted) {
      return "btn btn-success btn-sm ml-1";
    }
    if (noAccessToCurrentFlow) {
      return "btn btn-outline-success btn-sm ml-1";
    }
    return "btn btn-outline-secondary btn-sm ml-1";
  };

  /**
   * gets the button label
   * @author Muskan Thakur
   */
  const getButtonLabel = () => {
    if (isCompleted) {
      return "Closed";
    }
    if (noAccessToCurrentFlow) {
      return "Completed";
    }
    return "Complete";
  };
  return (
    <button
      id='mark-as-complete-icon'
      onClick={canComplete ? clickHandler : undefined}
      disabled={!canComplete}
      className={`${getButtonClass()} mark-as-complete-btn`}>
      {getButtonLabel()}
    </button>
  );
};

/** First Column Of Task Grid with ProjectColor(unpinned tasks), Drag Icon(pinned tasks), Complete Icon, Pin Icon and Edit Button */

export const Drag = (props) => {
  const taskGridContext = React.useContext(GridContext);
  const { dragRowProps } = taskGridContext;
  const { queueId } = useParams();
  const dispatch = useDispatch();
  const { IsLocked, Name, isFollowed, taskHistoryId, Id, IsPassedOn } = props.dataItem;
  const { user } = useSelector((state) => state.auth);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);

  /**
   * pins/unpins the respective task and updates tasks reducer
   */
  const pinTask = async () => {
    const payload = {
      userId: user?.id,
      tasks: [
        {
          TaskId: props.dataItem?.Id,
          active: props.dataItem?.pinned ? number.ZERO : number.ONE,
          projectId: taskGridContext.projectId ? taskGridContext.projectId : number.ZERO,
        },
      ],
    };
    setIsSaveDisabled(true);
    const [res, pinTaskCount] = await pinTaskHelper(payload);
    if (res) {
      dispatch(setPinTask(!props.dataItem?.pinned));
      dispatch(storePinTask(props.dataItem, pinTaskCount));
      setIsSaveDisabled(false);
    }
  };
  const handleSubmit = async () => {
    if (!isSaveDisabled) pinTask();
  };

  return (
    <td>
      {Name && (
        <div className='d-flex'>
          {dragRowProps?.isPinned ? <DragRowWithDragEnd {...props} /> : <></>}
          {!queueId && (
            <span
              className={`d-flex justify-content-center align-items-center hide task-grid-pin-icon cursor-pointer ${
                dragRowProps?.isPinned ? "pin-icon d-block" : ""
              }`}
              title={`${dragRowProps?.isPinned ? label.REMOVE_PIN : label.PIN_TASK}`}
              onClick={handleSubmit}
              disabled={isSaveDisabled}>
              {dragRowProps?.isPinned ? <span>{icon.PIN_TASK}</span> : <span className='unpin-pin-task-icon'>{icon.UNPIN_TASK}</span>}
            </span>
          )}


          {!!(queueId && props.dataItem.CurrentQueueId != queueId && !isFollowed && !isTrueBit(props.dataItem, "InApproval")) ? (
            <span className='delegation-icon d-flex cursor-default'>
              {icon.SENT_BY_DELEGATION}
              <span className='delegation-text cursor-default'>External</span>
            </span>
          ): (!isFollowed && !isTrueBit(props.dataItem, "InApproval") && queueId) && getCompleteIcon(props.dataItem)}
          {isTrueBit(props.dataItem, "InApproval") && !(user.id === props.dataItem.approvalAssignee) && (
            <span
              className='delegation-icon d-flex cursor-pointer'
              onClick={() => openSidebar({ id: Id, userId: user.id, taskHistoryId: taskHistoryId })}>
              {icon.APPROVAL_ICON}
              <span className='delegation-text'>Approval</span>
            </span>
          )}
          {isTrueBit(props.dataItem, "InApproval") && user.id === props.dataItem.approvalAssignee && (
            <span
              className='warning-badge approval-pending-style d-flex cursor-pointer'
              title='Pending for your Approval'
              onClick={() => openSidebar({ id: Id, userId: user.id, taskHistoryId: taskHistoryId })}>
              {icon.APPROVAL_ICON}
              <span className='delegation-text'>Pending</span>
            </span>
          )}
          {isFollowed && !isTrueBit(props.dataItem, "InApproval") ? (
            <span className='ml-1 opacity-7' data-toggle='tooltip' data-placement='bottom' title='View'>
              {icon.FOLLOWED}
            </span>
          ) : null}
          {!isFollowed && !isTrueBit(props.dataItem, "InApproval") && !queueId ? (
            getCompleteIcon(props.dataItem)
          ) : (
            <button className='link-open-button bg-transparent border-0 k-button k-button-md k-button-rectangle k-button-solid k-button-solid-base k-rounded-md k-icon-button k-group-start '></button>
          )}
        </div>
      )}
    </td>
  );
};

/**
 * Col 2 nudge button
 * @param {Object} props
 * @returns
 */
export const NudgeRow = (props) => {
  const { projectId, queueId } = useParams();
  const { allProjectsList } = useSelector((state) => state.tasks.defaultDetails);
  const { user } = useSelector((state) => state.auth);
  const requiredProjectId = useMemo(() => {
    return projectId ? projectId : user.myProjectId;
  }, [projectId, user.myProjectId]);
  const [nudgeDetails, setNudgeDetails] = useState({});
  const [userProjectsList, setUserProjectsList] = useState([]);

  /**
   * used to get projects which user has access to from all projects list
   */
  useEffect(() => {
    const userProjectList = allProjectsList?.filter((project) => {
      return project.RoleId || (project.IsPersonal && project.user === user.id);
    });
    setUserProjectsList(userProjectList);
  }, [allProjectsList]);

  /**
   * updates nudge details
   * @author {Shivam}
   */
  useEffect(() => {
    const { nudge, nudgeId, Assignee, CurrentProject, Id, IsCompleted, isFollowed } = props.dataItem;
    setNudgeDetails({
      userId: user.id,
      taskId: Id,
      projectId: CurrentProject,
      nudgeId: nudgeId,
      nudge: nudge,
      Assignee: Assignee,
      IsCompleted: IsCompleted,
      isFollowed: isFollowed,
    });
  }, [props.dataItem]);

  /**
   * provides the nudge icon
   * @param {None}
   * @returns {integer}
   * @author {Shivam}
   */
  const getNudgeIcon = useMemo(() => {
    const { nudge, nudgeId, Assignee, IsCompleted, isFollowed } = nudgeDetails;
    const isProjectAccess = userProjectsList?.find((item) => item.value === nudge);
    // task is nudged
    if (nudge && nudgeId) {
      if (queueId && isProjectAccess) {
        return number.ONE;
      }
      // user can acknowlege it
      if (isProjectAccess && nudge === parseInt(requiredProjectId)) {
        return number.ONE;
      }
      // can not acknowledge
      return number.TWO;
    } else {
      // user is assignee or follower can not nudge
      if (Assignee === user.id || isFollowed || !IsCompleted) {
        return number.THREE;
      }
      // can nudge
      return number.FOUR;
    }
  }, [nudgeDetails, userProjectsList, queueId, requiredProjectId, user.id]);

  return (
    <span id='nudge-icon-wrapper'>
      <NudgeButton
        nudgeTypeId={getNudgeIcon}
        nudgeDetails={nudgeDetails}
        setNudgeDetails={setNudgeDetails}
        onAfterNudge={props.onAfterNudge}
      />
    </span>
  );
};

/** Second Column with ProjectTaskId */

export const Code = (props) => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const { flowIcon, taskHistoryId, Id, WorkflowName, isForm, submittedFormId } = props.dataItem;
  const { expanded } = useSelector((state) => state.taskSidebar);
  const anchor = React.useRef(null);
  const [showLinkPopUp, setShowLinkPopUp] = useState(false);

  /** Handles right click functionality on task code of the grid
   * @author Sejal
   */
  const handleRighClick = (event) => {
    event.preventDefault();
    setShowLinkPopUp(!showLinkPopUp);
  };

  /**
   * Open WorkflowElements in task Sidebar
   */
  const handleOpenWorkflowElements = async () => {
    await openSidebar({ id: Id, userId: user?.id, taskHistoryId: taskHistoryId });
    await dispatch(setExpanded([...expanded, "task-workflow"]));
    // await dispatch(setScrollSection("task-workflow"));
    const delay = number.ONE_THOUSAND_FIVE_HUNDRED;
    setTimeout(() => {
      var elmnt = document.getElementById("task-workflow");
      elmnt.scrollIntoView();
    }, delay);
  };

  const handleCodeClick = async () => {
    if (isForm && !submittedFormId) {
      const response = await dispatch(getTaskDetails({ taskId: props.dataItem.Id, userId: user.id, taskHistoryId: props.dataItem.taskHistoryId }));
      dispatch(setTask(response?.taskDetails));
      if (response) {
        dispatch(setShowFormPopup(true));
      }
    } else {
      openSidebar({ id: props.dataItem.Id, userId: user.id, taskHistoryId: props.dataItem.taskHistoryId });
    }
  };

  return (
    <td>
      {props.dataItem.Name && (
        <div className='d-flex align-items-center'>
          <span className='ml-1 d-flex justify-content-center align-items-center'>
            {flowIcon ? (
              <span
                onClick={handleOpenWorkflowElements}
                className='opacity-7 d-flex justify-content-center align-items-center cursor-pointer blue mr-1'
                title={WorkflowName}>
                <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
                  {icon[flowIcon]}
                </Tooltip>
              </span>
            ) : (
              <div className='workflow-blank-icon h-auto'></div>
            )}
          </span>
          <span
            ref={anchor}
            onContextMenu={handleRighClick}
            className='text-center cursor-pointer task-id blue'
            id='task-grid-code-on-click'
            onClick={() => {
              handleCodeClick();
            }}>
            {props.dataItem.ProjectTaskId}
          </span>
        </div>
      )}
      {showLinkPopUp && <NewTabPopUp anchor={anchor} showLinkPopUp={showLinkPopUp} setShowLinkPopUp={setShowLinkPopUp} taskId={props.dataItem.Id} />}
    </td>
  );
};

/** Third Column with Task Name */

export const TaskName = (props) => {
  const { Name, isFollowed } = props.dataItem;
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const { task, showTaskSidebar } = useSelector((state) => state.taskSidebar);
  const [name, setName] = useState(Name);
  useEffect(() => {
    if (Name?.trim() !== name?.trim()) {
      setName(Name);
    }
  }, [Name]);

  const changeTaskName = (event) => {
    setName(event.target.value);
  };
  const saveName = (event) => {
    if (event.target.value?.trim() && event.target.value?.trim() != Name) {
      updateGrid(event.target.value, props.dataItem, projectId, "name");
      if (showTaskSidebar && props.dataItem?.Id === task?.taskId) dispatch(setTaskOnBlur({ key: "Name", value: event.target.value }));
    } else {
      if (!name?.trim()) {
        getNotification(quote.ENTER_TASK_NAME_PROJECT, notifyIcon.WARNING_ICON);
        setName(Name);
      }
    }
  };
  const saveOnEnter = (event) => {
    if (event.key == "Enter") {
      if (event.target.value?.trim() && event.target.value?.trim() != Name) {
        updateGrid(event.target.value, props.dataItem, projectId, "name");
        document.getElementById("task-grid-name").blur();
      } else {
        if (!name?.trim()) {
          getNotification(quote.ENTER_TASK_NAME_PROJECT, notifyIcon.WARNING_ICON);
          setName(Name);
        }
      }
    }
  };
  let defaultRendering = null;
  if (!props.dataItem.inEdit || props.dataItem.inEdit !== props.field || (isFollowed && !isTrueBit(props.dataItem, "InApproval"))) {
    defaultRendering = (
      <td>
        {Name && (
          <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
            <div className='text-truncate' title={name}>
              <span>{name}</span>
            </div>
          </Tooltip>
        )}
      </td>
    );
  } else {
    defaultRendering = (
      <td>
        {Name && (
          <Input
            autoFocus
            id='task-grid-name'
            className='form-control py-1'
            value={name}
            onChange={changeTaskName}
            onBlur={saveName}
            onKeyPress={saveOnEnter}
          />
        )}
      </td>
    );
  }
  return props.render ? props.render.call(null, defaultRendering, props) : defaultRendering;
};

/** 9 Column with tags of pinTask */
export const TaskTags = (props) => {
  const [isRef, setIsRef] = useState(false);
  const tagsRef = useRef();
  /**
   * determines whether the tags column will be formatted according to the column with
   */
  useEffect(() => {
    setIsRef(true);
  }, [tagsRef.current, tagsRef.current?.offsetWidth]);

  return (
    <td className='tags-column' ref={tagsRef} key={props.dataItem.Id}>
      {props.dataItem.Name && isRef && formatTags(props.dataItem?.tagIds, props.dataItem, tagsRef.current?.offsetWidth)}
    </td>
  );
};

/** Fifth Column Showing IsReturnable and Comments Counts */
export const Icons = (props) => {
  const taskGridContext = React.useContext(GridContext);
  const dispatch = useDispatch();
  const { newCommentCount, commentCount, Name } = props.dataItem;
  const titles = newCommentCount > number.ZERO ? quote.NEW_COMMENTS : commentCount > number.ZERO ? quote.VIEW_PING : quote.ADD_PING;
  const commentClasses = newCommentCount > number.ZERO ? "red" : commentCount > number.ZERO ? "blue" : "";
  const { user } = useSelector((state) => state.auth);
  const handleCommentIcon = async () => {
    await openSidebar({ id: props.dataItem.Id, userId: user.id, taskHistoryId: props.dataItem.taskHistoryId });
    dispatch(getFocusedSection("comments"));
  };
  if (Name) {
    return (
      <td className='icons-column text-left'>
        <span className='d-flex align-items-center justify-content-start gap-2'>
          <span className={`task-grid-nudge-icon`}>
            <NudgeRow {...props} />
          </span>
          <ApprovalStatus {...props} />
          {!!props?.dataItem?.ShowComments && (
            <span
              onClick={handleCommentIcon}
              title={titles}
              className={`${
                newCommentCount !== number.ZERO ? "show" : "hide"
              } ${commentClasses} cursor-pointer grid-comment-icon d-flex align-items-start justify-content-center opacity-7`}>
              {icon.COMMENT}
              <span className=''>{newCommentCount > number.ZERO ? newCommentCount : null}</span>
            </span>
          )}
        </span>
      </td>
    );
  } else return <></>;
};

/** Sixth Column Showing DueDate of Tasks */

export const DueDate = (props) => {
  const { DueDate, Name, isFollowed, IsTaskComplete, IsTaskClosed, recurrenceId } = props.dataItem;
  const { projectId, queueId } = useParams();
  const [value, setValue] = useState(new Date(DueDate));
  const dispatch = useDispatch();
  const { lastFilterId, teamSavedFilters } = useSelector((state) => state.teamFilter);
  const { showTaskSidebar, task } = useSelector((state) => state.taskSidebar);
  const dropdownRef = useRef(null);

  useClickOutside(props, dropdownRef);

  const archivedView = useMemo(() => {
    const archivedFilter = teamSavedFilters?.find((filter) => filter.value === lastFilterId)?.label;
    return archivedFilter === label.ARCHIVED;
  }, [teamSavedFilters, lastFilterId]);

  useEffect(() => {
    DueDate ? setValue(recurrenceId ? getDateAtMidnight(DueDate) : new Date(DueDate)) : setValue(null);
  }, [DueDate]);
  const changeDueDate = (event) => {
    if (new Date(value).valueOf() != new Date(event.value).valueOf()) {
      setValue(new Date(event.value));
      updateGrid(event.value, props.dataItem, projectId, "dueDate");
      props.dataItem.inEdit = false;
      if (showTaskSidebar && props.dataItem?.Id === task?.taskId) dispatch(setTaskOnBlur({ key: "DueDate", value: new Date(event.value) }));
    }
  };
  let defaultRendering = null;
  if (!props.dataItem.inEdit || props.dataItem.inEdit !== props.field || (isFollowed && !isTrueBit(props.dataItem, "InApproval"))) {
    defaultRendering = (
      <td className='due-date-column text-left cursor-pointer'>
        <span className={dateDiff(DueDate) && !archivedView && !(IsTaskComplete || IsTaskClosed) ? "comment-box d-flex red" : "comment-box d-flex"}>
          {value ? (!queueId ? formatDateWithoutTimeZone(value) : formatDateWithoutYearWithoutTimeZone(value)) : null}
        </span>
      </td>
    );
  } else {
    defaultRendering = (
      <td className='due-date-column text-left' ref={dropdownRef}>
        {props.dataItem.IsCompleted?.data[number.ZERO] == number.ZERO ? (
          <DatePicker
            defaultValue={value}
            id='dropdown-task-grid-duedate'
            defaultShow={true}
            onChange={changeDueDate}
            format={dateFormat.DEFAULT_DATE_FORMAT}
            placeholder={label.NO_DUE_DATE}
            draggable={false}
            onKeyDown={(e) => e.preventDefault()}
          />
        ) : (
          <span className={dateDiff(DueDate) && !archivedView && (!IsTaskComplete || !IsTaskClosed) ? "comment-box d-flex red" : "comment-box d-flex"}>
            {value ? (!queueId ? formatDateWithoutTimeZone(value)  : formatDateWithoutYearWithoutTimeZone(value)) : null}
          </span>
        )}
      </td>
    );
  }
  if (Name) {
    return props.render ? props.render.call(null, defaultRendering, props) : defaultRendering;
  } else return <></>;
};

/** 7 Column Showing Priority of Tasks */

export const Priority = (props) => {
  const state = store.getState();
  const priorityList = state.tasks.defaultDetails.priorityList;
  const { Priority, Name, isFollowed } = props.dataItem;
  const [priority, setPriority] = useState(priorityList?.find((c) => c.value === Priority));
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const { showTaskSidebar, task } = useSelector((state) => state.taskSidebar);
  const dropdownRef = useRef(null);

  useClickOutside(props, dropdownRef);

  const handlePriorityChange = (event) => {
    setPriority(event.target.value);
    updateGrid(event.target.value.value, props.dataItem, projectId, "priority");
    props.dataItem.inEdit = false;
    if (showTaskSidebar && props.dataItem?.Id === task?.taskId) dispatch(setTaskOnBlur({ key: "Priority", value: event.target.value?.value }));
  };

  useEffect(() => {
    setPriority(priorityList?.find((c) => c.value === Priority));
  }, [Priority, priorityList]);

  const itemRender = (li, itemProps) => {
    const itemChildren = <span> {icon[itemProps.dataItem.icon]} </span>;
    return React.cloneElement(li, li.props, itemChildren);
  };

  const valueRender = (element, value) => {
    if (!value) {
      return element;
    }
    const children = [<span> {icon[value.icon]} </span>];
    return React.cloneElement(element, { ...element.props }, children);
  };

  let defaultRendering = null;
  if (!props.dataItem.inEdit || props.dataItem.inEdit !== props.field || (isFollowed && !isTrueBit(props.dataItem, "InApproval"))) {
    defaultRendering = <td className='priority-column text-center cursor-pointer'>{icon[priority?.icon]}</td>;
  } else {
    defaultRendering =
      props.dataItem.IsCompleted?.data[number.ZERO] === number.ZERO ? (
        <td className='priority-column text-center' ref={dropdownRef}>
          <DropDownList
            popupSettings={{ className: "priority-custom-popup" }}
            onChange={handlePriorityChange}
            id='dropdown-task-grid-priority'
            itemRender={itemRender}
            value={priority}
            data={priorityList}
            textField='label'
            valueRender={valueRender}
          />
        </td>
      ) : (
        <td className='priority-column text-center'>{icon[priority?.icon]}</td>
      );
  }
  if (Name) {
    return props.render ? props.render.call(null, defaultRendering, props) : defaultRendering;
  } else return <></>;
};

/** 8 Column Showing Stages of Tasks on My Task Page */

export const Stage = (props) => {
  const { StageId, IsLocked, Name, isFollowed, IsPassedOn, StageName, Id, approvalStatus } = props.dataItem;
  const { projectId } = useParams();
  const [stages, setStages] = useState([]);
  const [stage, setStage] = useState(null);
  const dispatch = useDispatch();
  const { allTasks } = useSelector((state) => state.tasks);
  const { showTaskSidebar, task, stages: currentTaskStages } = useSelector((state) => state.taskSidebar);
  const dropdownRef = useRef(null);
  const { defaultDetails } = useSelector((store) => store.tasks);
  const status = defaultDetails?.approvalStatus?.find((action) => action.StatusId === approvalStatus);

  useClickOutside(props, dropdownRef);

  useEffect(() => {
    setStage(stages?.find((c) => c?.value == StageId));
  }, [stages]);

  /**
   *  used to get stages for individual tasks for editing stages from grid
   * @author {Prachi Jain}
   */
  useEffect(() => {
    (async () => {
      if (
        (props.dataItem.inEdit === "StageId" || props.dataItem.inEdit === "Stages" || props.dataItem.inEdit === "currentStage") &&
        !(IsLocked.data[number.ZERO] || isFollowed || isTrueBit(props.dataItem, "InApproval"))
      ) {
        const stages = await getProjectStages({ projectId: props.dataItem.EntityProjectId, taskId: props.dataItem.Id });
        setStages(stages);
      }
    })();
  }, [props.dataItem.inEdit]);

  /**
   * handles stage change from grid on change in dropdown
   * @param {*} event
   * @author {Himanshu Negi}
   */
  const handleStageChange = (event) => {
    setStage(event.target.value);
    const payload = setStageName(allTasks, event.target.value, Id);
    store.dispatch(setNewMyTasks(payload));
    updateGrid(event.target.value.value, props.dataItem, projectId, "stageId");
    props.dataItem.inEdit = false;
    if (showTaskSidebar && props.dataItem?.Id === task?.taskId) {
      dispatch(setTaskOnBlur({ key: "StageId", value: event.target.value?.value }));
      dispatch(setTaskOnBlur({ key: "currentStage", value: event.target.value?.value }));
      const internalAssignee = currentTaskStages?.find((item) => item?.value == event.target.value?.value)?.defaultAssignee;
      if (internalAssignee) dispatch(setTaskOnBlur({ key: "Assignee", value: internalAssignee }));
    }
  };

  const itemRender = (li, itemProps) => {
    const itemChildren = (
      <span key={itemProps?.index} className='w-100'>
        <span title={itemProps.dataItem.label} className='w-100 d-block text-truncate'>
          {itemProps.dataItem.label}
        </span>
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  };

  const isDelegated = IsLocked?.data[number.ZERO] && !isFollowed && !IsPassedOn;

  // Return empty cell if approval status exists and is not approved or task is delegated
  if ((approvalStatus && status?.Status !== "Approved") || (isDelegated && Name)) {
    const emptyRendering = <td className='stage-column text-left'></td>;
    return props.render ? props.render.call(null, emptyRendering, props) : emptyRendering;
  }

  let defaultRendering = null;

  if (
    !props.dataItem.inEdit ||
    props.dataItem.inEdit !== props.field ||
    IsLocked.data[number.ZERO] ||
    isFollowed ||
    isTrueBit(props.dataItem, "InApproval")
  ) {
    defaultRendering = (
      <td className='stage-column text-left cursor-pointer'> {IsLocked?.data[number.ZERO] && !IsPassedOn ? label.DELEGATED : StageName}</td>
    );
  } else {
    defaultRendering =
      props.dataItem.IsCompleted?.data[number.ZERO] === number.ONE || stages.length === number.ZERO ? (
        <td className='stage-column text-left'> {IsLocked?.data[number.ZERO] && !IsPassedOn ? label.DELEGATED : StageName}</td>
      ) : (
        <td className='stage-column text-left' ref={dropdownRef}>
          <DropDownList
            onChange={handleStageChange}
            id='dropdown-task-grid-stage'
            value={stage}
            defaultValue={{ label: StageName, value: StageId }}
            itemRender={itemRender}
            data={stages}
            textField='label'
          />
        </td>
      );
  }
  if (Name) {
    return props.render ? props.render.call(null, defaultRendering, props) : defaultRendering;
  } else return <></>;
};

/** 8 Column Showing Assignee of Tasks on My Project Task Page */

export const Assignee = (props) => {
  const state = store.getState(),
    assigneeList = state.tasks.defaultDetails.assigneeList,
    projectList = state.tasks.defaultDetails.allProjectsList,
    { projectId } = useParams(),
    { Assignee, AssigneePhotos, AssigneeName, IsLocked, Name } = props.dataItem,
    [internalAssignee, setInternalAssignee] = useState([]),
    [assignee, setAssignee] = useState(internalAssignee?.find((c) => c.value == Assignee));
  const dispatch = useDispatch();
  const { showTaskSidebar, task } = useSelector((state) => state.taskSidebar);
  const { user } = useSelector((state) => state.auth);
  const dropdownRef = useRef(null);

  useClickOutside(props, dropdownRef);

  const approvalAssignee = useMemo(() => {
    return assigneeList?.find(({ value }) => value === props.dataItem?.approvalAssignee);
  }, [assigneeList, props.dataItem?.approvalAssignee]);

  useEffect(() => {
    setInternalAssignee(filterAssignees(assigneeList, props.dataItem, projectId, projectList));
  }, [assigneeList]);

  useEffect(() => {
    setInternalAssignee(filterAssignees(assigneeList, props.dataItem, projectId, projectList));
  }, []);

  useEffect(() => {
    setAssignee(internalAssignee?.find((c) => c.value == Assignee));
  }, [Assignee, internalAssignee]);

  const handleAssigneeChange = (event) => {
    setAssignee(event.target.value);
    updateGrid(event.target.value.value, props.dataItem, projectId, "assignedId");
    props.dataItem.inEdit = false;
    if (showTaskSidebar && props.dataItem?.Id === task?.taskId) dispatch(setTaskOnBlur({ key: "Assignee", value: event.target.value?.value }));
  };

  const renderProfilePicture = () => {
    if (isTrueBit(props.dataItem, "InApproval") && props.dataItem?.approvalAssignee) {
      return approvalAssignee?.PhotoLinks?.small ? (
        <img className='profile-picture rounded-circle object-fit-cover mr-2' src={approvalAssignee?.PhotoLinks?.small} alt='Approval Assignee' />
      ) : (
        <span className={`mr-2 profile-icon ${!approvalAssignee?.label && "unassigned-profile"}`}>
          {!approvalAssignee?.label ? icon.UNASSIGNED_USER : icon.ACCOUNT_CIRCLE}
        </span>
      );
    }

    if (AssigneePhotos?.small) {
      return <img className='profile-picture rounded-circle object-fit-cover mr-2' src={AssigneePhotos.small} alt='Assignee' />;
    }

    return (
      <span className={`mr-2 profile-icon ${!AssigneeName && "unassigned-profile"}`}>
        {!AssigneeName ? icon.UNASSIGNED_USER : icon.ACCOUNT_CIRCLE}
      </span>
    );
  };

  let defaultRendering = null;
  if (!props.dataItem.inEdit || props.dataItem.inEdit !== props.field || IsLocked.data[number.ZERO] || isTrueBit(props.dataItem, "InApproval")) {
    defaultRendering = (
      <td className='cursor-pointer'>
        <div className='d-flex align-items-center'>
          {renderProfilePicture()}
          <span className='text-truncate'>
            {isTrueBit(props.dataItem, "InApproval") && props.dataItem?.approvalAssignee
              ? approvalAssignee?.label ?? label.UNASSIGNED
              : AssigneeName ?? label.UNASSIGNED}
          </span>
        </div>
      </td>
    );
  } else {
    defaultRendering = (
      <td ref={dropdownRef}>
        <DropDownList onChange={handleAssigneeChange} id='dropdown-task-grid-assignee' value={assignee} data={internalAssignee} textField='label' />
      </td>
    );
  }
  if (Name) {
    return props.render ? props.render.call(null, defaultRendering, props) : defaultRendering;
  } else return <></>;
};

/** Second Column with ProjectTaskId */

export const Projects = (props) => {
  const taskGridContext = React.useContext(GridContext);
  const { selectedFilterName } = taskGridContext;
  const state = store.getState();
  const { allProjectsList } = state.tasks.defaultDetails;
  const { operationalTeamName, id } = state.auth.user;

  const [userProject, setUserProject] = useState();
  const [assignedByProjectName, setAssignedByProjectName] = useState();
  const projectTitle = props.dataItem.Projects == userProject ? prependString(operationalTeamName, label.MY) : props.dataItem.Projects;

  useEffect(() => {
    const userProjectsList = allProjectsList?.filter((project) => {
      return project.RoleId || (project.IsPersonal && project.user === id);
    });
    if (userProjectsList) {
      let project = userProjectsList.find((p) => p.IsPersonal == number.ONE);
      setUserProject(project?.label);
      setAssignedByProjectName(userProjectsList.find((p) => p.ProjectId == props.dataItem.ApprovalAssignedByProject)?.ProjectName);
    }
  }, [allProjectsList, selectedFilterName]);

  return (
    <td>
      {props.dataItem.Name && (
        <div className='text-truncate' title={projectTitle}>
          {selectedFilterName === label.APPROVAL_INITIATED_FILTER ? assignedByProjectName : projectTitle}
        </div>
      )}
    </td>
  );
};

/**
 * 9th Column Showing Assigned By field of tasks
 * @author Muskan Thakur
 */
export const AssignedBy = (props) => {
  const { AssignedByPhotos, AssignedByName, CurrentRelatedAssignedType } = props.dataItem;
  const isEmailTask = !AssignedByName && CurrentRelatedAssignedType === 5;
  return (
    <td>
      {AssignedByName && (
        <div className='d-flex align-items-center'>
          {AssignedByPhotos?.small ? (
            <img className='profile-picture rounded-circle object-fit-cover mr-2' src={AssignedByPhotos?.small} alt='Assigned By' />
          ) : (
            <span className={`mr-2 profile-icon opacity-7 ${!AssignedByName && "unassigned-profile"}`} alt='Assigned By'>
              {!AssignedByName ? icon.UNASSIGNED_USER : icon.ACCOUNT_CIRCLE}
            </span>
          )}
          <span className='text-truncate'>{AssignedByName ?? label.UNASSIGNED}</span>
        </div>
      )}
      {isEmailTask && <div>{label.AUTOMATION_TASK}</div>}
    </td>
  );
};

/** Fourth Column Showing approval status on my task page
 * @param {Void}
 * @returns JSX
 */
export const ApprovalStatus = (props) => {
  //here
  const { defaultDetails } = useSelector((store) => store.tasks);
  const status = defaultDetails?.approvalStatus?.find((action) => action.StatusId === props.dataItem?.approvalStatus);
  return (
    <div className='ml-1 d-flex justify-content-center align-items-center'>
      <Tooltip position='bottom' anchorElement='target' parentTitle={true}>
        {status ? (
          <span className='approval-originator' title={status?.Status}>
            <span className='d-flex align-items-center justify-content-center cursor-default opacity-7'>{icon[status?.StatusIcon]}</span>
          </span>
        ) : (
          <span className='approval-originator'>
            <span className='d-flex align-items-center justify-content-center cursor-default invisible approval-icon-placeholder'>
              {icon.APPROVAL_INFO}
            </span>
          </span>
        )}
      </Tooltip>
    </div>
  );
};

/** Component for showing Teams Component consiting currentStage and currentProject for queueTaskPage
 * @author Pragun Gandotra
 */
export const Teams = (props) => {
  const currentStage = props.dataItem.StageName;
  const currentProject = props.dataItem.Projects;
  return (
    <td>
      <div className='d-flex'>
        {currentStage && (
          <span className='stage-box d-inline-block text-truncate' title={currentStage}>
            {currentStage}
          </span>
        )}{" "}
        <span className='text-truncate' title={currentProject}>
          {currentProject}
        </span>
      </div>
    </td>
  );
};

/** Component for showing ReadOnlyAssignee Component for queueTaskPage
 * @author Pragun Gandotra
 */
export const ReadOnlyAssignee = (props) => {
  const { AssigneePhotos, AssigneeName } = props.dataItem;
  return (
    <td>
      <div className='d-flex justify-content-center'>
        {AssigneePhotos?.small ? (
          <img className='profile-picture rounded-circle object-fit-cover' src={AssigneePhotos?.small} title={AssigneeName} alt='Assignee' />
        ) : (
          <span className={`mr-2 profile-icon ${!AssigneeName && "unassigned-profile"}`} title={AssigneeName ?? label.UNASSIGNED}>
            {!AssigneeName ? icon.UNASSIGNED_USER : icon.ACCOUNT_CIRCLE}
          </span>
        )}
      </div>
    </td>
  );
};

export const CreatedDate = (props) => {
  const CreatedOn = props?.dataItem?.CreatedOn;
  return (
    <td>
      <span className='text-truncate'>{formatDate(CreatedOn)}</span>
    </td>
  );
};

export const ApprovalInitiator = (props) => {
  const { ApprovalAssignedBy } = props.dataItem;
  const state = store.getState();
  const { assigneeList } = state.tasks.defaultDetails;
  const ApprovalInitiator = assigneeList?.find((assignee) => assignee.value === ApprovalAssignedBy)?.label;
  return (
    <td>
      <span className='text-truncate'>{ApprovalInitiator}</span>
    </td>
  );
};